/**
 * Locale-like pattern
 *   Language (required)
 *     2-3 alpha chars
 *   Group (optional)
 *     1+ -_ separator chars
 *     Region (required)
 *       2 alpha chars OR 3 num chars
 *   Group (optional)
 *     1+ -_ separator chars
 *     Brand (required)
 *       1+ alpha chars
 * @type {RegExp}
 */
const localeLikePattern = /^([a-zA-Z]{2,3})(?:[-_]+([a-zA-Z]{2}|[0-9]{3}))?(?:[-_]+([a-zA-Z]+))?$/;

/**
 * Locale pattern
 *   Language (required)
 *     2-3 lower alpha chars
 *   Separator
 *     1 _ char
 *   Region (required)
 *     2 upper alpha chars OR 3 num chars
 * @type {RegExp}
 */
const localePattern = /^([a-z]{2,3})_([A-Z]{2}|[0-9]{3})$/;

/**
 * Locale Formatted pattern
 *   Language (required)
 *     2-3 lower alpha chars
 *   Separator
 *     1 - char
 *   Region (required)
 *     2 upper alpha chars OR 3 num chars
 * @type {RegExp}
 */
const localeFormattedPattern = /^([a-z]{2,3})-([A-Z]{2}|[0-9]{3})$/;

/**
 * Locale Full pattern
 *   Language (required)
 *     2-3 lower alpha chars
 *   Separator
 *     1 _ char
 *   Region (required)
 *     2 upper alpha chars OR 3 num chars
 *   Separator
 *     1 _ char
 *   Brand (required)
 *     1+ upper alpha chars
 * @type {RegExp}
 */
const localeFullPattern = /^([a-z]{2,3})_([A-Z]{2}|[0-9]{3})_([A-Z]+)$/;

const LOCALE_FALLBACKS = {
    'no-NO': 'nb-NO',
    no: 'nb'
};

function isLocaleLike(locale) {
    return !!locale && localeLikePattern.test(locale);
}

function isLocale(locale) {
    return !!locale && localePattern.test(locale);
}

function isLocaleFormatted(locale) {
    return !!locale && localeFormattedPattern.test(locale);
}

function isLocaleFull(locale) {
    return !!locale && localeFullPattern.test(locale);
}

function getLocaleLikeParts(locale) {
    let [, language, region, brand] = localeLikePattern.exec(locale);

    if (language) {
        language = language.toLowerCase();
    }

    if (region) {
        region = region.toUpperCase();
    }

    if (brand) {
        brand = brand.toUpperCase();
    }

    return {language, region, brand};
}

function getCombinedLocaleParts(parts, separator) {
    const {language, region, brand} = parts;

    return [language, region, brand].filter(Boolean).join(separator);
}

/**
 * Site-specific locale (eg: 'en_US')
 * @param locale
 * @returns {*}
 */
function getGeneratedLocale(locale) {
    if (isLocaleLike(locale)) {
        if (!isLocale(locale)) {
            const {language, region} = getLocaleLikeParts(locale);

            return getCombinedLocaleParts({language, region}, '_');
        }

        return locale;
    }

    return undefined; // eslint-disable-line no-undefined
}

/**
 * BCP-47 compatible locale (eg: 'en-US')
 * This includes providing a fallback for cases when using a valid locale yields no formatting. (eg: 'nb-NO')
 * @param locale
 * @returns {*}
 */
function getGeneratedLocaleFormatted(locale) {
    if (isLocaleLike(locale)) {
        if (!isLocaleFormatted(locale)) {
            const {language, region} = getLocaleLikeParts(locale);
            const formattedLocale = getCombinedLocaleParts({language, region}, '-');
            return LOCALE_FALLBACKS[formattedLocale] || formattedLocale;
        }

        return LOCALE_FALLBACKS[locale] || locale;
    }

    return undefined; // eslint-disable-line no-undefined
}

/**
 * Site-specific moniker locale (eg: 'en_US_HA')
 * @param locale
 * @returns {*}
 */
function getGeneratedLocaleFull(locale) {
    if (isLocaleLike(locale)) {
        if (!isLocaleFull(locale)) {
            const {language, region, brand} = getLocaleLikeParts(locale);

            return getCombinedLocaleParts({language, region, brand}, '_');
        }

        return locale;
    }

    return undefined; // eslint-disable-line no-undefined
}

function getLanguage(locale) {
    if (isLocaleLike(locale)) {
        return getLocaleLikeParts(locale).language;
    }

    return undefined; // eslint-disable-line no-undefined
}

function getRegion(locale) {
    if (isLocaleLike(locale)) {
        return getLocaleLikeParts(locale).region;
    }

    return undefined; // eslint-disable-line no-undefined
}

function getBrand(locale) {
    if (isLocaleLike(locale)) {
        return getLocaleLikeParts(locale).brand;
    }

    return undefined; // eslint-disable-line no-undefined
}

export {
    getGeneratedLocale,
    getGeneratedLocaleFormatted,
    getGeneratedLocaleFull,
    getLanguage,
    getRegion,
    getBrand
};
